/**
 * Pure focal-framing geometry shared by the editor and every cover renderer.
 *
 * The persisted X/Y values identify the natural-image pixel that belongs at
 * the frame centre. They are not CSS object-position percentages. Rendering
 * therefore cover-fits the natural image, applies the persisted relative zoom,
 * centres that image pixel, then constrains the translation so the frame stays
 * covered.
 */

export const MIN_FOCAL_SCALE = 1;
export const MAX_FOCAL_SCALE = 3;

export interface FocalPointData {
  focalPointX: number;
  focalPointY: number;
  focalPointScale: number;
}

export interface ImageFrameGeometry {
  containerWidth: number;
  containerHeight: number;
  naturalWidth: number;
  naturalHeight: number;
}

export interface ImageTransform {
  x: number;
  y: number;
  /** Absolute CSS scale from natural-image pixels to rendered pixels. */
  scale: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function finiteOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function hasValidGeometry(geometry: ImageFrameGeometry): boolean {
  return (
    isPositiveFinite(geometry.containerWidth) &&
    isPositiveFinite(geometry.containerHeight) &&
    isPositiveFinite(geometry.naturalWidth) &&
    isPositiveFinite(geometry.naturalHeight)
  );
}

function centeredFocalPoint(): FocalPointData {
  return { focalPointX: 50, focalPointY: 50, focalPointScale: MIN_FOCAL_SCALE };
}

export function coverFitScale(geometry: ImageFrameGeometry): number {
  if (!hasValidGeometry(geometry)) return 0;

  const { containerWidth, containerHeight, naturalWidth, naturalHeight } = geometry;
  return Math.max(containerWidth / naturalWidth, containerHeight / naturalHeight);
}

export function constrainCoverPosition(
  x: number,
  y: number,
  scale: number,
  geometry: ImageFrameGeometry,
): { x: number; y: number } {
  if (
    !hasValidGeometry(geometry) ||
    !isPositiveFinite(scale) ||
    !Number.isFinite(x) ||
    !Number.isFinite(y)
  ) {
    return { x: 0, y: 0 };
  }

  const scaledWidth = geometry.naturalWidth * scale;
  const scaledHeight = geometry.naturalHeight * scale;
  const minX = Math.min(geometry.containerWidth - scaledWidth, 0);
  const minY = Math.min(geometry.containerHeight - scaledHeight, 0);

  return {
    x: clamp(x, minX, 0),
    y: clamp(y, minY, 0),
  };
}

export function focalPointToImageTransform(
  geometry: ImageFrameGeometry,
  focalPointX: number | null | undefined,
  focalPointY: number | null | undefined,
  focalPointScale: number | null | undefined,
): ImageTransform {
  const coverScale = coverFitScale(geometry);
  if (!isPositiveFinite(coverScale)) return { x: 0, y: 0, scale: 0 };

  // One missing axis means there is no complete saved focal. In that case the
  // contract is centred cover, including a reset to 1x relative zoom.
  const hasFocal =
    typeof focalPointX === 'number' &&
    Number.isFinite(focalPointX) &&
    typeof focalPointY === 'number' &&
    Number.isFinite(focalPointY);
  const xPercent = hasFocal ? clamp(finiteOr(focalPointX, 50), 0, 100) : 50;
  const yPercent = hasFocal ? clamp(finiteOr(focalPointY, 50), 0, 100) : 50;
  const relativeScale = hasFocal
    ? clamp(finiteOr(focalPointScale, MIN_FOCAL_SCALE), MIN_FOCAL_SCALE, MAX_FOCAL_SCALE)
    : MIN_FOCAL_SCALE;
  const scale = coverScale * relativeScale;

  const desiredX = geometry.containerWidth / 2 - (xPercent / 100) * geometry.naturalWidth * scale;
  const desiredY = geometry.containerHeight / 2 - (yPercent / 100) * geometry.naturalHeight * scale;
  const constrained = constrainCoverPosition(desiredX, desiredY, scale, geometry);

  return { ...constrained, scale };
}

export function imageTransformToFocalPoint(
  geometry: ImageFrameGeometry,
  x: number,
  y: number,
  scale: number,
): FocalPointData {
  const coverScale = coverFitScale(geometry);
  if (
    !isPositiveFinite(coverScale) ||
    !isPositiveFinite(scale) ||
    !Number.isFinite(x) ||
    !Number.isFinite(y)
  ) {
    return centeredFocalPoint();
  }

  const imageCenterX = (geometry.containerWidth / 2 - x) / scale;
  const imageCenterY = (geometry.containerHeight / 2 - y) / scale;

  const focalPointX = (imageCenterX / geometry.naturalWidth) * 100;
  const focalPointY = (imageCenterY / geometry.naturalHeight) * 100;
  const focalPointScale = scale / coverScale;
  if (
    !Number.isFinite(focalPointX) ||
    !Number.isFinite(focalPointY) ||
    !Number.isFinite(focalPointScale)
  ) {
    return centeredFocalPoint();
  }

  return {
    focalPointX: clamp(focalPointX, 0, 100),
    focalPointY: clamp(focalPointY, 0, 100),
    focalPointScale: clamp(focalPointScale, MIN_FOCAL_SCALE, MAX_FOCAL_SCALE),
  };
}
